/**
 * cadastreGeom — client-side lookup of parcel geometries for DVF comparables.
 *
 * DVF transactions carry `parcelIds` (l_idpar) but no geometry. We want to
 * draw those parcels on the fiche's satellite map, so we resolve each idpar
 * to its GeoJSON Polygon/MultiPolygon via the `getParcelGeometries` callable.
 *
 * The hook is shaped as a list-in / map-out so callers can build the map
 * in a single render once everything is resolved. Partial loading is
 * exposed so a caller can show a spinner until the whole batch is back.
 *
 * Caching is handled server-side (/cadastre/{idpar} in Firestore). The client
 * doesn't retry on its own — a failed batch surfaces as `status: "error"`
 * and the UI can offer the user a "retry" button if needed.
 */

import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

// Keep in sync with functions/src/getParcelGeometries.ts `ParcelGeom`.
export type ParcelGeom = {
  idpar: string;
  geometry: unknown; // GeoJSON Polygon / MultiPolygon
  section: string;
  numero: string;
  prefixe: string;
  insee: string;
  surface_m2: number;
};

type Output = {
  geometries: Record<string, ParcelGeom | null>;
};

const callGetParcelGeometries = httpsCallable<
  { idpars: string[] },
  Output
>(functions, "getParcelGeometries");

export async function fetchParcelGeometries(
  idpars: string[],
): Promise<Record<string, ParcelGeom | null>> {
  if (idpars.length === 0) return {};
  const res = await callGetParcelGeometries({ idpars });
  return res.data.geometries;
}

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; geometries: Record<string, ParcelGeom | null> }
  | { status: "error"; message: string };

/**
 * Resolve a list of idpars to their GeoJSON geometries. Lazy — the callable
 * isn't invoked until `idpars` is a non-empty array. Deduplicates and
 * normalises idpars before firing so two re-renders with the same logical
 * set don't cause a refetch.
 */
export function useParcelGeometries(idpars: string[] | null): State {
  const normalized = useMemo(() => {
    if (!idpars || idpars.length === 0) return null;
    const deduped = Array.from(
      new Set(
        idpars
          .filter((s): s is string => typeof s === "string")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
      ),
    );
    return deduped.length > 0 ? deduped : null;
  }, [idpars]);

  // A stable key for the `useEffect` dep — arrays are compared by identity so
  // we coalesce into a string.
  const key = normalized ? normalized.slice().sort().join("|") : null;

  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!normalized || !key) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetchParcelGeometries(normalized)
      .then((geometries) => {
        if (cancelled) return;
        setState({ status: "ready", geometries });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setState({ status: "error", message: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
